import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import { push_undo } from 'data/lib/undo'
import { PromiseWatch } from "./lib/promise-helpers"
import { local } from 'data/lib/local-json'
import validationService from "data/validate"

// private module logic //

const id: string = "asset-attributes"
const initialState: ApiResult<object[]> = { id, state: "init", value: [] }
const assetAttributeStream = new BehaviorSubject<ApiResult<object[]>>(initialState)
const assetAttributesEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})

const profile = local("auth.profile")
const orgId   = _.get(profile, "organizationId");

let assetAttributeMap = {}
const _fetches_ = PromiseWatch()
const _patches_ = PromiseWatch()

function clear(): void {
  assetAttributeStream.next({ id, state: 'ok', value: [] })
  assetAttributeMap = {}
}

function get(assetAttributeid: number): object {
  return assetAttributeMap[assetAttributeid]
}

function refresh(assetid: number): Promise<any> {
  return fetch(assetid)
}

function fetch(
  assetid: number,
  limit: number = 0,
  offset: number = 0
): Promise<object[] | { message: string }> {

  clear()
  assetAttributeStream.next({ id, state: 'busy', value: assetAttributeStream.getValue().value })

  // if (!assetid) debugger // how is this ever happening?
  let url = `assetattribute?assetid=eq.${assetid}`
  if (limit && offset >= 0) {
    url += `&limit=${limit}&offset=${offset}`
  }

  return Client.get(url)
  .then((records: object[]) => {
    _.forEach(records, (assetAttribute) => (assetAttributeMap[assetAttribute.assetAttributeid] = assetAttribute))
    assetAttributeStream.next({ id, state: 'ok', value: records })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  })
}

function patch(assetAttribute: {
  assetid: number,
  assetattributeid: number,
  attributeid: number,
  value: string
}): Promise<object | { message: string }> {
  const {assetid, assetattributeid, attributeid, value} = assetAttribute
  const currentValue: object[] = assetAttributeStream.getValue().value
  const currentAssetAttribute = _.find(currentValue, { attributeid })
  if (currentAssetAttribute && value == currentAssetAttribute.value) return new Promise(() => currentValue)
  const verb = _.isEmpty(value) ? 'del' : _.isEmpty(currentAssetAttribute) ? 'post' : 'patch'

  push_undo({
    undofunc: () => patch(currentAssetAttribute && currentAssetAttribute),
    redofunc: () => patch(assetAttribute)
  })

  // optimistic update
  assetAttributeStream.next({
    id,
    state: 'ok',
    value: verb == 'del' ? _.reject(currentValue, {attributeid}) : _.unionBy([assetAttribute], currentValue, 'attributeid')
  })


  let promise
  switch(verb){
    case 'del':
      promise = Client.del(`assetattribute?assetid=eq.${assetid}&attributeid=eq.${attributeid}`)
      break
    case 'patch':
      promise = Client.patch(`assetattribute?assetattributeid=eq.${assetattributeid}&assetid=eq.${assetid}&attributeid=eq.${attributeid}`, {value})
      break
    case 'post':
      promise = promise = Client.post(`assetattribute`, {organizationid: orgId, value, assetid, attributeid})
      break
  }

  return _patches_.watch(attributeid, promise
    .then((assetattribute: object[]) => {
      _.forEach(assetattribute, (assetattribute) => (assetAttributeMap[assetattribute.assetattributeid] = assetattribute))
      validationService.fetchByParent('assetattribute', 'asset',  assetattribute['assetid'])
      return assetattribute[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      assetAttributeStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetch(assetid)
      return err
    }))
}


// public module interface //

const AssetAttributesService = {
  stream: () => assetAttributeStream.asObservable(),
  value: () => assetAttributeStream.getValue().value,
  eventStream: () => assetAttributesEventStream.asObservable(),
  clear,
  get,
  fetch,
  patch,
  refresh
}

export default AssetAttributesService


